import TotalField from './totalfield';
import Autogen from './autogen';
const slugify = require('slugify')

//-----------------------------------------------
// Total CMS ID Field Automation
//-----------------------------------------------
export default class Identifier extends TotalField {

    constructor(container, settings) {
        // Define option defaults
        const defaults = {
            autogen : false,
        };
        settings = Object.assign({}, defaults, settings);

		super(container, settings);

		this.valid = false;

		if (this.settings.autogen) {
			this.autogen = new Autogen(this);
		}

		// Check if we're editing an existing item (form has an ID)
		// For deck items, only lock if the item was loaded with existing data
		// (has data-item-id attribute), not if it was just autogenerated
		const deckItemId = this.isInDeck ? this.deckItem.getAttribute('data-item-id') : null;
		const isExistingDeckItem = this.isInDeck && deckItemId && deckItemId.length > 0;

		if (
			(this.form.id && this.form.id.length > 0 && !this.isInDeck && !this.form.isTemplateForm()) ||
			isExistingDeckItem
		) {
			// The ID cannot be changed when editing (except templates which support rename/move)
			this.disable();
			this.valid = true; // ID is valid in edit mode since it can't be changed
		}
		if (this.getValue() !== "" && !this.isLocked() && !this.form.isTemplateForm()) {
			this.validateIdExists();
		}
		if (this.settings.autogen && !this.isLocked() && this.getValue() === "") {
			this.setValue(this.autogenId());
			this.validateIdExists();
		}
    }

	changed() {
		// Don't update form ID when in deck context (deck items have their own IDs)
		if (!this.isInDeck) {
			this.form.setId(this.getValue());
		}
		// don't trigger change events for ID field
		// turning this on will cause infinite event loops
		return;
	}

	// Override TotalField.changeListener
	changeListener() {
		if (this.settings.autogen && !this.isLocked()) {
			this.autogen = this.autogen || new Autogen(this);
			this.autogen.attachListeners(() => {
				if (this.isLocked()) return;
				this.setValue(this.autogenId());
				this.validateIdExists();
			});
		}
        // Check ID changes directly
        this.input.addEventListener("input",  e => this.lock(), {once: true});
		this.input.addEventListener("change", e => this.validateIdExists());

		// Re-validate when category changes (template forms only)
		if (this.form.isTemplateForm()) {
			const categoryField = this.form.form.querySelector('select[name="category"]');
			if (categoryField) {
				categoryField.addEventListener("change", () => this.validateIdExists());
			}
		}
	}

	autogenId() {
		const raw = this.autogen.generate();
		const slugified = this.slugify(raw);

		// For deck context, replace hyphens with underscores for Twig dot notation compatibility
		return this.isInDeck ? slugified.replace(/-/g, '_') : slugified;
	}

	getCollectionCount() {
		// Get the collection count from the form's data-collection-count attribute
		// This represents the next OID that should be used for new objects
		const count = this.form.form.getAttribute('data-collection-count');
		return count ? parseInt(count, 10) + 1 : 1;
	}

	generateUuid() {
		// Generate UUID v4 (random) - RFC 4122 compliant
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
			const r = Math.random() * 16 | 0;
			const v = c == 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}

	disable() {
		this.lock();
		return this.input.setAttribute("disabled", true);
	}

	lock() {
		return this.container.classList.add("locked");
	}

	isLocked() {
		return this.container.classList.contains("locked") || this.input.hasAttribute("disabled");
	}

    slugify(id) {
		id = id.replace('@', '-at-').replace(/\./g, '-');

		// Build the remove regex, allowing custom characters if specified
		let removeRegex = /[*+~.,()?'"!:@{}\[\]\/\\]/g;
		if (this.settings.allowCharacters) {
			// Escape special regex characters in the allowed string
			const escaped = this.settings.allowCharacters.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			// Remove allowed characters from the default remove pattern
			const pattern = `[*+~.()'"!:@{}\\[\\]\\\\${escaped.includes('/') ? '' : '/'}]`;
			removeRegex = new RegExp(pattern, 'g');
		}

        return slugify(id, {
			replacement : '-', // replace spaces with replacement character, defaults to `-`
			remove      : removeRegex, // remove characters that match regex, defaults to `undefined`
			lower       : true, // convert to lower case, defaults to `false`
			strict      : false, // strip special characters except replacement, defaults to `false`
			trim        : true, // trim leading and trailing replacement chars, defaults to `true`
			// locale      : 'vi', // language code of the locale to use
		});
    }

    idExists() {
		this.valid = false;
		this.form.setId("");
		console.warn("ID already exists: "+this.getValue());
        this.container.classList.remove("unsaved");
        this.container.classList.add("error");
		this.input.setCustomValidity("ID already exists");
    }

    idAvailable() {
		this.valid = true;
		this.form.setId(this.getValue());
		this.form.unsaved();
        this.container.classList.remove("error");
        this.container.classList.add("unsaved");
		this.input.setCustomValidity("");
    }

	updateNonIDProperty() {
		this.valid = true;
		this.form.unsaved();
    }

	validate() {
		if (!this.isVisible()) return true;

		// For deck items with empty IDs, this might be acceptable if they're new
		if (this.isInDeck && this.getValue() !== "") {
			this.valid = true;
		}

		// If ID has a value and no custom validity error, assume valid
		// (handles race condition where validateIdExists API call hasn't responded yet)
		if (this.getValue() !== "" && this.input.validationMessage === "") {
			this.valid = true;
		}

		if (this.valid && this.input.checkValidity()) {
			return true;
		}

		this.error(this.input.validationMessage);
		return false;
	}

    validateIdExists() {
		// slugify the value to ensure it's a valid ID
		const id = this.slugify(this.getValue());
		if (!id) return;

		// Set the slugified value
		this.setValue(id);

		// If the property is not ID, do not check for existence
		// This is for when you use and ID field on a non-ID property
		if (this.property !== "id") {
			this.updateNonIDProperty();
			return;
		}

		// If we are in a deck item, we don't need to check for ID existence
		if (this.isInDeck) return;

		let api = `/collections/${this.form.collection}/${id}`;

		if (this.form.isCollectionForm()) {
			api = `/collections/${id}`;
		}
		if (this.form.isSchemaForm()) {
			api = `/schemas/${id}`;
		}
		if (this.form.isTemplateForm()) {
			const categoryField = this.form.form.querySelector('select[name="category"]');
			const category = categoryField ? categoryField.value : '';
			api = category ? `/templates/${category}/${id}` : `/templates/${id}`;
		}

        this.api.existsAPI(api).then(response => {
			// Handle network errors or undefined response gracefully
			if (!response) {
				this.idAvailable(); // Assume available on network error
				return;
			}

			// If editing a template and the path matches the original, it's the same file
			if (response.ok && this.form.isTemplateEditMode() && this.form.route === api) {
				this.idAvailable();
				return;
			}

			response.ok ? this.idExists() : this.idAvailable();
		});
    }
}
